// useDocumentSearch — debounced, cancellable query hook for
// GET /api/v1/documents/search (CTSK-493 §5.2; design doc §6.2/§6.3).
//
// Pattern mirrors UserTypeahead (CTSK-245): the raw input string is
// debounced into `debouncedQuery`, and TanStack Query keys on the
// debounced value. Cancellation comes from React Query — it hands
// `queryFn` an AbortSignal and aborts the in-flight fetch whenever the
// key changes (new keystroke past the debounce) or the component
// unmounts; a cancelled request's result is discarded, never written to
// state. `retry: false` because design doc §3.7 says no auto-retry — the
// error state owns the Retry button via `refetch()`.
//
// Request/response types are the shared contract Phase 1a published
// (packages/shared/src/types/document-search.ts). Nothing is re-declared
// here.

import { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  DOCUMENT_SEARCH_LIMIT_DEFAULT,
  DOCUMENT_SEARCH_QUERY_MAX,
  DOCUMENT_SEARCH_QUERY_MIN,
  type DocumentSearchResponse,
  type DocumentSearchResult,
} from '@captain-tasko/shared';
import { apiFetch } from '../../../lib/api.js';

/** Design doc §3.2 — 250ms after the last keystroke. */
export const DOCUMENT_SEARCH_DEBOUNCE_MS = 250;

export interface UseDocumentSearchOptions {
  /** Raw input text. Debounced + trimmed before it reaches the network. */
  query: string;
  /** Nest scope (slug or code) or null for all nests. */
  nest?: string | null;
  /** Feature flag / mount gate — false means never fetch. Default true. */
  enabled?: boolean;
  debounceMs?: number;
  limit?: number;
}

export interface UseDocumentSearchState {
  /** The trimmed, debounced query the current `data` answers. */
  query: string;
  /** True once the query is long enough to search (≥2 chars) and enabled. */
  active: boolean;
  data: DocumentSearchResponse | undefined;
  results: DocumentSearchResult[];
  /** First load for this key (no data yet). */
  isLoading: boolean;
  /** Any fetch in flight, including background refetch. */
  isFetching: boolean;
  isError: boolean;
  error: unknown;
  /** Re-fire the last query (error-state Retry). */
  refetch: () => void;
}

/** Normalize what the user typed into what the API accepts. */
export function normalizeQuery(raw: string): string {
  return raw.trim().slice(0, DOCUMENT_SEARCH_QUERY_MAX);
}

export function useDocumentSearch({
  query,
  nest = null,
  enabled = true,
  debounceMs = DOCUMENT_SEARCH_DEBOUNCE_MS,
  limit = DOCUMENT_SEARCH_LIMIT_DEFAULT,
}: UseDocumentSearchOptions): UseDocumentSearchState {
  const normalized = normalizeQuery(query);
  const [debounced, setDebounced] = useState(normalized);

  useEffect(() => {
    if (normalized.length < DOCUMENT_SEARCH_QUERY_MIN) {
      // Clearing / too-short bypasses the debounce so the dropdown
      // collapses immediately (UserTypeahead does the same for '').
      setDebounced(normalized);
      return;
    }
    const id = window.setTimeout(() => setDebounced(normalized), debounceMs);
    return () => window.clearTimeout(id);
  }, [normalized, debounceMs]);

  const active = enabled && debounced.length >= DOCUMENT_SEARCH_QUERY_MIN;

  const q = useQuery({
    queryKey: ['document-search', debounced, nest, limit],
    queryFn: ({ signal }) =>
      apiFetch<DocumentSearchResponse>('/documents/search', {
        query: { q: debounced, limit, nest: nest ?? undefined, page: 1 },
        signal,
      }),
    enabled: active,
    retry: false,
    // Keep the previous page of results on screen while the next
    // keystroke's request is in flight — no dropdown flicker.
    placeholderData: (prev) => prev,
    staleTime: 5_000,
  });

  return {
    query: debounced,
    active,
    data: active ? q.data : undefined,
    results: active ? (q.data?.data.results ?? []) : [],
    isLoading: active && q.isLoading,
    isFetching: active && q.isFetching,
    isError: active && q.isError,
    error: q.error,
    refetch: () => { void q.refetch(); },
  };
}
